/**
 * Accueil PWA Surveillant — grille de cartes simples (appel, justifs, EDT, conduite, dashboard).
 * Conçue pour usage rapide sur tablette/téléphone — gros boutons, peu de texte.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import type { RootState } from '../../store';
import {
  ClipboardCheck, FileCheck, Calendar, Award, BarChart3, User, Bell,
} from 'lucide-react';
import { SurveillantLayout } from '../SurveillantLayout';

interface Carte {
  to: string;
  icon: any;
  titre: string;
  description: string;
  couleur: string;
}

const CARTES: Carte[] = [
  {
    to: '/surveillant/appel',
    icon: ClipboardCheck,
    titre: 'Appel du jour',
    description: 'Marquer les présences et absences',
    couleur: 'bg-blue-600 hover:bg-blue-700',
  },
  {
    to: '/surveillant/justifications',
    icon: FileCheck,
    titre: 'Justifications',
    description: 'Valider les justificatifs d\'absence',
    couleur: 'bg-emerald-600 hover:bg-emerald-700',
  },
  {
    to: '/surveillant/edt',
    icon: Calendar,
    titre: 'Emploi du temps',
    description: 'Consulter et modifier les créneaux',
    couleur: 'bg-indigo-600 hover:bg-indigo-700',
  },
  {
    to: '/surveillant/conduite',
    icon: Award,
    titre: 'Conduite',
    description: 'Saisir des observations sur les élèves',
    couleur: 'bg-amber-600 hover:bg-amber-700',
  },
  {
    to: '/surveillant/absences',
    icon: BarChart3,
    titre: 'Dashboard absences',
    description: 'Statistiques et tendances',
    couleur: 'bg-purple-600 hover:bg-purple-700',
  },
];

export default function AccueilSurveillant() {
  const navigate = useNavigate();
  const user = useSelector((s: RootState) => s.auth.user);
  const [heure, setHeure] = useState(new Date());

  useEffect(() => {
    const t = setInterval(() => setHeure(new Date()), 30_000);
    return () => clearInterval(t);
  }, []);

  const salutation = (() => {
    const h = heure.getHours();
    if (h < 12) return 'Bonjour';
    if (h < 18) return 'Bon après-midi';
    return 'Bonsoir';
  })();

  return (
    <SurveillantLayout>
      <div className="space-y-4">
        {/* En-tête personnalisé */}
        <div className="bg-gradient-to-br from-blue-900/40 to-slate-900 border border-blue-800/50 rounded-xl p-4">
          <div className="text-xs text-blue-300 uppercase tracking-wider font-semibold">
            {salutation}
          </div>
          <div className="text-xl font-bold text-white mt-1">
            {user?.prenoms || ''} {user?.nom || ''}
          </div>
          <div className="text-xs text-slate-400 mt-1">
            {heure.toLocaleDateString('fr-FR', {
              weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
            })} · {heure.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
          </div>
        </div>

        {/* Grille de cartes — 2 cols mobile, 3 cols tablette */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {CARTES.map((c) => {
            const Icon = c.icon;
            return (
              <button
                key={c.to}
                onClick={() => navigate(c.to)}
                className={`${c.couleur} text-white p-4 rounded-xl shadow-md transition-all active:scale-95 text-left flex flex-col items-start gap-2 min-h-[120px]`}
              >
                <Icon size={28} className="flex-shrink-0" />
                <div className="font-bold text-sm leading-tight">{c.titre}</div>
                <div className="text-[10px] opacity-90 leading-snug">{c.description}</div>
              </button>
            );
          })}
        </div>

        {/* Footer compact */}
        <div className="text-center pt-2">
          <button
            onClick={() => navigate('/surveillant/profil')}
            className="text-xs text-slate-400 hover:text-white inline-flex items-center gap-1.5"
          >
            <User size={12} /> Mon profil
          </button>
        </div>
      </div>
    </SurveillantLayout>
  );
}
